import { type RefObject, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { useIsomorphicLayoutEffect } from './hooks/useIsomorphicLayoutEffect';

/**
 * Top safe-area ownership for the native iOS shell (#2028, #2053).
 *
 * The shell paints edge-to-edge (`viewport-fit=cover`, capacitor `contentInset:
 * 'never'`), so the status-bar / Dynamic Island band is page territory. The
 * shared page header (renderer/Header.tsx) fills it with a spacer inside its
 * opaque bar, and so do the other inset-bearing top chromes (marketing
 * header/banner, AuthShell, the support-chat conversation bar and its list
 * skeleton). Any view that reaches the layout's <main> with NO such owner (a
 * Header-less page like the order page for signed-in users, the editor, a
 * suspense/skeleton fallback) would otherwise start at y=0 under the status bar.
 *
 * ## Regions, not one global count (#2053)
 *
 * Ownership used to be a single module-level counter toggling one attribute on
 * `main[data-url]`: ANY mounted chrome anywhere cancelled the layout's fallback
 * for the WHOLE page. That is wrong for a page with more than one top chrome
 * side by side. Support chat renders two panes at once (DMPageWrapper) — the
 * conversation bar and the conversation list — and the conversation bar's
 * spacer stripped <main>'s fallback padding for the sibling list pane, which was
 * still showing an inset-less skeleton under the Dynamic Island (#2053).
 *
 * So ownership is tracked per *region*: `main[data-url]` is the root region, and
 * any element marked `data-safe-top-region` (each support-chat pane) is a nested
 * one. A chrome claims the region it is rendered in AND every region above it,
 * up to <main>. The CSS in renderer/styles/index.css then reads:
 *
 *  - `main[data-url]:not([data-safe-top-owned])` — nothing on the page carries
 *    the inset, so <main> pads its top and paints an opaque status-bar backdrop.
 *    Nested regions add nothing here, so the page still gets exactly one inset.
 *  - `main[data-url][data-safe-top-owned] [data-safe-top-region]:not(...owned)`
 *    — some pane does carry the inset (so <main> adds nothing), and this sibling
 *    pane does not, so it pads itself.
 *
 * Every combination yields exactly one inset, which keeps the #2030 invariant
 * that a page whose header IS mounted never renders a double inset.
 *
 * The attributes are toggled imperatively (per-element counts in a WeakMap +
 * layout effect, no React state) so they never re-render the layout, and they
 * are set before paint, so a page whose header mounts after a loading state
 * doesn't flash a double inset.
 *
 * Why not a pure-CSS `main:not(:has([data-safe-top]))`? WKWebView (iOS 26)
 * left `main` with a stale padding after the owner mounted — `matches()`
 * reported the selector as no longer matching while the computed padding stayed
 * at the inset — so the header pages rendered a double inset. Toggling an
 * attribute on the region itself invalidates reliably.
 *
 * env() is 0 on web / Android / non-notch devices, so every rule here is inert
 * there (0px padding, 0px-tall backdrop, 0px spacer).
 */

export const SAFE_TOP_OWNED_ATTR = 'data-safe-top-owned';
/** Marks an element as its own top-chrome region (a pane with its own chrome). */
export const SAFE_TOP_REGION_ATTR = 'data-safe-top-region';

const ROOT_SELECTOR = 'main[data-url]';
const REGION_SELECTOR = `${ROOT_SELECTOR}, [${SAFE_TOP_REGION_ATTR}]`;

/** How many mounted chromes currently carry the inset for each region. */
const ownerCounts = new WeakMap<Element, number>();

/**
 * The region the chrome lives in, plus every region above it up to <main>.
 * Claiming the whole chain is what keeps <main> from adding a second inset over
 * a pane that already carries its own.
 */
function regionChain(anchor: Element | null): Element[] {
  if (typeof document === 'undefined') {
    return [];
  }

  const chain: Element[] = [];
  let region = anchor?.closest(REGION_SELECTOR) ?? null;

  while (region) {
    chain.push(region);
    region = region.parentElement?.closest(REGION_SELECTOR) ?? null;
  }

  // A chrome with no anchor (a hook-only owner) or one rendered outside every
  // region (a portal) still covers the page, so it claims the root region.
  return chain.length ? chain : Array.from(document.querySelectorAll(ROOT_SELECTOR));
}

function claim(region: Element, delta: number) {
  const next = (ownerCounts.get(region) ?? 0) + delta;

  ownerCounts.set(region, next);
  region.toggleAttribute(SAFE_TOP_OWNED_ATTR, next > 0);
}

/**
 * Marks the calling component as carrying the top safe-area inset itself.
 *
 * Pass `anchor` — a ref to a node the chrome renders — so the claim lands on
 * that chrome's own region on a multi-pane page. Without one the claim goes to
 * the root region, which is right for a page with a single top chrome.
 */
export function useSafeTopOwner(active = true, anchor?: RefObject<Element | null>) {
  useIsomorphicLayoutEffect(() => {
    if (!active) {
      return;
    }

    const regions = regionChain(anchor?.current ?? null);

    for (const region of regions) {
      claim(region, 1);
    }

    return () => {
      for (const region of regions) {
        claim(region, -1);
      }
    };
  }, [active, anchor]);
}

/**
 * Renders an inert marker; registers its parent chrome as the inset owner of the
 * region it sits in. The marker is `hidden` (display: none), so it never affects
 * layout — it only gives the claim a position in the DOM to resolve its region
 * from.
 */
export function SafeTopOwner() {
  const ref = useRef<HTMLSpanElement>(null);

  useSafeTopOwner(true, ref);

  return <span ref={ref} hidden />;
}

/**
 * The status-bar spacer for an opaque top bar: a block the height of the inset
 * placed INSIDE the bar (a spacer, not padding, so callers' `py-*` overrides
 * can't drop it), which also registers the bar as its region's inset owner.
 */
export function SafeTopSpacer({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useSafeTopOwner(true, ref);

  return (
    <div ref={ref} aria-hidden className={twMerge('h-[env(safe-area-inset-top,0px)]', className)} />
  );
}
